import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
// Clerk Auth (replacing Firebase)
import { ClerkAuthProvider } from "@/components/auth/ClerkAuthProvider";
import { ProtectedRoute, PublicRoute } from "@/components/auth/ClerkProtectedRoute";
import { SuperAdminRoute } from "@/components/auth/SuperAdminRoute";
import AdminLayout from "@/components/admin/AdminLayout";
import { ThemeProvider } from "@/components/theme/ThemeProvider";
import { ForceLightMode } from "@/components/theme/ForceLightMode";
import Dashboard from "./pages/Dashboard";
import {
  SettingsLayout,
  AccountSettings,
  CompanySettings,
  WidgetSettings,
  IntegrationsSettings,
  BillingSettings,
} from "./pages/settings";
// Clerk auth pages
import SignIn from "./pages/ClerkSignIn";
import SignUp from "./pages/ClerkSignUp";
import Onboarding from "./pages/Onboarding";
import WidgetPage from "./pages/WidgetPage";
import Landing from "./pages/Landing";
import NotFound from "./pages/NotFound";
import WidgetInstallation from "./pages/docs/WidgetInstallation";

const queryClient = new QueryClient();

// Admin pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AuditLog from "./pages/admin/AuditLog";
import TenantList from "./pages/admin/TenantList";
import TenantDetail from "./pages/admin/TenantDetail";
import TenantLeads from "./pages/admin/TenantLeads";
import CreateTenant from "./pages/admin/CreateTenant";

const App = () => (
  <ThemeProvider>
  <ClerkAuthProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* Public auth routes - always light mode */}
            <Route
              path="/sign-in/*"
              element={
                <ForceLightMode>
                  <PublicRoute>
                    <SignIn />
                  </PublicRoute>
                </ForceLightMode>
              }
            />
            <Route
              path="/sign-up/*"
              element={
                <ForceLightMode>
                  <PublicRoute>
                    <SignUp />
                  </PublicRoute>
                </ForceLightMode>
              }
            />

            {/* Onboarding route (authenticated but no tenant check) */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute requireTenant={false}>
                  <Onboarding />
                </ProtectedRoute>
              }
            />

            {/* Public landing page - always light mode */}
            <Route path="/" element={<ForceLightMode><Landing /></ForceLightMode>} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AccountSettings />} />
              <Route path="account" element={<AccountSettings />} />
              <Route path="company" element={<CompanySettings />} />
              <Route path="widget" element={<WidgetSettings />} />
              <Route path="integrations" element={<IntegrationsSettings />} />
              <Route path="billing" element={<BillingSettings />} />
            </Route>

            {/* Public widget route - no auth required */}
            <Route path="/widget/:tenantId" element={<WidgetPage />} />

            {/* Documentation routes */}
            <Route path="/docs/widget-installation" element={<WidgetInstallation />} />

            {/* Admin routes - super admin only */}
            <Route
              path="/admin"
              element={
                <SuperAdminRoute>
                  <AdminLayout />
                </SuperAdminRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="tenants" element={<TenantList />} />
              <Route path="tenants/new" element={<CreateTenant />} />
              <Route path="tenants/:id" element={<TenantDetail />} />
              <Route path="tenants/:id/leads" element={<TenantLeads />} />
              <Route path="audit-log" element={<AuditLog />} />
            </Route>

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ClerkAuthProvider>
  </ThemeProvider>
);

export default App;
